import React, { useMemo, useState } from "react";
import { DownloadResultsService } from "@application/useCases";
import { FileMetadata } from "@domain/schemas/BI";
import { ArtifactDownloadDialog } from "./ArtifactDownloadDialog";

type DownloadTarget = {
  title: string;
  loader: () => Promise<FileMetadata[]>;
  downloader: (selectedFiles: string[]) => Promise<string>;
};

type UseArtifactDownloadDialogProps = {
  downloadResultsService: DownloadResultsService;
  triggerDownloads: (downloadUrl: string) => void;
};

export const useArtifactDownloadDialog = ({ downloadResultsService, triggerDownloads }: UseArtifactDownloadDialogProps) => {
  const [target, setTarget] = useState<DownloadTarget | null>(null);
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openDialog = async (nextTarget: DownloadTarget) => {
    setTarget(nextTarget);
    setFiles([]);
    setErrorMessage(null);
    setIsLoading(true);

    try {
      const availableFiles = await nextTarget.loader();
      setFiles(availableFiles);
      if (availableFiles.length === 0) {
        setErrorMessage("No downloadable files were found.");
      }
    } catch {
      setErrorMessage("Failed to load downloadable files.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeDialog = () => {
    setTarget(null);
    setFiles([]);
    setErrorMessage(null);
    setIsLoading(false);
  };

  const dialog = useMemo(() => {
    if (!target) {
      return null;
    }

    return (
      <ArtifactDownloadDialog
        title={target.title}
        files={files}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onClose={closeDialog}
        onDownload={async (selectedFiles) => {
          setErrorMessage(null);
          try {
            const downloadUrl = await target.downloader(selectedFiles);
            triggerDownloads(downloadUrl);
            closeDialog();
          } catch {
            setErrorMessage("Failed to start download.");
          }
        }}
      />
    );
  }, [errorMessage, files, isLoading, target, triggerDownloads]);

  return {
    artifactDownloadDialog: dialog,
    openJobDownloadDialog: async (jobId: string) =>
      openDialog({
        title: "Download job results",
        loader: () => downloadResultsService.listJobResultFiles(jobId),
        downloader: (selectedFiles) => downloadResultsService.getDownloadURL(jobId, selectedFiles),
      }),
    openStepDownloadDialog: async (jobId: string, jobPipelineStepId: string) =>
      openDialog({
        title: "Download step artifacts",
        loader: () => downloadResultsService.listStepArtifactFiles(jobId, jobPipelineStepId),
        downloader: (selectedFiles) => downloadResultsService.getStepDownloadURL(jobId, jobPipelineStepId, selectedFiles),
      }),
    openBatchDownloadDialog: async (jobId: string, jobPipelineStepId: string, batchId: string) =>
      openDialog({
        title: "Download batch artifacts",
        loader: () => downloadResultsService.listBatchArtifactFiles(jobId, jobPipelineStepId, batchId),
        downloader: (selectedFiles) => downloadResultsService.getBatchDownloadURL(jobId, jobPipelineStepId, batchId, selectedFiles),
      }),
  };
};
